import { RULES, EFFECTS } from "./movement.mjs";
import { generateFrontline, generateMap, PROFILES } from "./mapgen.mjs";

const T = (tiles, list, id) => { for (const [q, r] of list) tiles[`${q},${r}`] = id; };

export const MAPS = {
  skirmish: { label: "Skirmish", detail: "11×11 duel · 5 AP · 24 rounds", domains: ["land"], effects: [], size: "11×11" },
  frontline: {
    label: PROFILES.frontline.label,
    detail: PROFILES.frontline.detail,
    domains: PROFILES.frontline.domains,
    effects: PROFILES.frontline.effects,
    size: "36×24",
  },
  archipelago: {
    label: PROFILES.archipelago.label,
    detail: PROFILES.archipelago.detail,
    domains: PROFILES.archipelago.domains,
    effects: PROFILES.archipelago.effects,
    size: "36×24",
  },
  highlands: {
    label: PROFILES.highlands.label,
    detail: PROFILES.highlands.detail,
    domains: PROFILES.highlands.domains,
    effects: PROFILES.highlands.effects,
    size: "36×24",
  },
  "grand-theater": {
    label: PROFILES["grand-theater"].label,
    detail: PROFILES["grand-theater"].detail,
    domains: PROFILES["grand-theater"].domains,
    effects: PROFILES["grand-theater"].effects,
    size: "60×40",
  },
};

export { EFFECTS, PROFILES };

/** { map, seed, effects? } -> a fresh match state. */
export function createMatch(opts = {}) {
  const kind = opts.map ?? "skirmish";
  if (kind === "skirmish") return createSkirmish(opts);
  if (PROFILES[kind]) return generateMap(kind, { seed: opts.seed ?? 1, effects: opts.effects });
  if (kind === "frontline") return generateFrontline({ seed: opts.seed ?? 1, effects: opts.effects });
  return createSkirmish(opts);
}

function createSkirmish(opts = {}) {
  const tiles = {};
  T(tiles, [[3, 1], [5, 2], [2, 5], [7, 4], [4, 7], [8, 8], [1, 8], [9, 3]], "ore");
  T(tiles, [[4, 4], [5, 5], [6, 3]], "ruins");
  T(tiles, [[5, 4], [6, 5], [6, 6], [4, 6], [3, 5], [7, 7]], "mountain");
  // Flank plateaus and jump-pad towers over spike pits, point-mirrored so both sides get the same routes.
  T(tiles, [[1, 5], [0, 6], [1, 6], [9, 5], [10, 4], [9, 4], [2, 8], [4, 8], [8, 2], [6, 2]], "plateau");
  T(tiles, [[3, 8], [7, 2]], "pit");
  const L = (kind, a, b) => ({ kind, a: { q: a[0], r: a[1] }, b: { q: b[0], r: b[1] } });
  const links = [
    L("stairs", [2, 4], [1, 5]), L("ladder", [2, 6], [1, 6]),
    L("stairs", [8, 6], [9, 5]), L("ladder", [8, 4], [9, 4]),
    L("stairs", [2, 9], [2, 8]), L("ladder", [5, 7], [4, 8]), L("jump", [2, 8], [4, 8]),
    L("stairs", [8, 1], [8, 2]), L("ladder", [5, 3], [6, 2]), L("jump", [8, 2], [6, 2]),
  ];

  return {
    board: { qCount: 11, rCount: 11 },
    tiles,
    links,
    map: { kind: "skirmish" },
    domains: ["land"],
    effects: [...new Set(opts.effects ?? [])],
    firstPlayer: 1,
    structures: [],
    bases: [
      { owner: 1, q: 1, r: 1, hp: RULES.baseHp, hpMax: RULES.baseHp },
      { owner: 2, q: 9, r: 9, hp: RULES.baseHp, hpMax: RULES.baseHp },
    ],
    players: {
      1: { faction: "GVS", credits: 1500 },
      2: { faction: "BOE", credits: 1500 },
    },
    units: [
      { id: "gvs-tank", type: "tank", owner: 1, q: 2, r: 2, facing: 1, hp: 70, moved: 0, attacked: false, fresh: false },
      { id: "gvs-inf1", type: "infantry", owner: 1, q: 1, r: 2, facing: 1, hp: 30, moved: 0, attacked: false, fresh: false },
      { id: "gvs-inf2", type: "infantry", owner: 1, q: 2, r: 1, facing: 1, hp: 30, moved: 0, attacked: false, fresh: false },
      { id: "gvs-art", type: "artillery", owner: 1, q: 0, r: 2, facing: 1, hp: 40, moved: 0, attacked: false, fresh: false },
      { id: "gvs-harv", type: "harvester", owner: 1, q: 3, r: 1, facing: 1, hp: 50, moved: 0, attacked: false, fresh: false },
      { id: "boe-tank", type: "tank", owner: 2, q: 7, r: 8, facing: 4, hp: 70, moved: 0, attacked: false, fresh: false },
      { id: "boe-inf1", type: "infantry", owner: 2, q: 9, r: 8, facing: 4, hp: 30, moved: 0, attacked: false, fresh: false },
      { id: "boe-inf2", type: "infantry", owner: 2, q: 8, r: 9, facing: 4, hp: 30, moved: 0, attacked: false, fresh: false },
      { id: "boe-art", type: "artillery", owner: 2, q: 10, r: 8, facing: 4, hp: 40, moved: 0, attacked: false, fresh: false },
      { id: "boe-harv", type: "harvester", owner: 2, q: 8, r: 8, facing: 4, hp: 50, moved: 0, attacked: false, fresh: false },
    ],
    reserves: { 1: [], 2: [] },
    oreOwner: {},
    seq: 0,
    timeExpired: false,
    turn: { player: 1, ap: RULES.apPerTurn, coordinatedAdvanceUsed: false, number: 1, round: 1 },
  };
}
